import { useQuery, useMutation } from '@tanstack/react-query'
import { useSearchParams } from 'react-router-dom'
import { queryKeys, QUERY_CONFIG } from '../../lib/react-query'
import { queryClient } from '../../lib/react-query/queryClient'
import { targetsApi } from '../../services/api'
import { useAuthStore } from '../../store/authStore'
import { toast } from 'sonner'
import type { TargetApiResponse, TargetCreateType } from '../../app/modules/types/target'

/**
 * Hook for fetching paginated targets list
 * Automatically refetches when URL params change
 */
export function useTargets() {
  const orgId = useAuthStore((state) => state.orgId)
  const [searchParams] = useSearchParams()
  const params = searchParams.toString()

  return useQuery({
    queryKey: queryKeys.targets.list(orgId, params),
    queryFn: () => targetsApi.getList(params),
    enabled: !!orgId,
  })
}

/**
 * Hook for fetching target types (for create target form)
 * @deprecated /targets/types/ endpoint removed - types are auto-detected from seed
 * This hook is kept for backward compatibility but will always return empty array
 */
export function useTargetTypes() {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.targetTypes.list(orgId),
    queryFn: async () => [] as TargetCreateType[],
    staleTime: QUERY_CONFIG.LISTS.staleTime,
    enabled: false, // Disabled since endpoint is removed
  })
}

/**
 * Hook for fetching available target types for a specific workflow
 */
export function useWorkflowTargetTypes(workflowId: string | null) {
  return useQuery({
    queryKey: ['workflow', 'availableTypes', workflowId || ''],
    queryFn: () => targetsApi.getWorkflowAvailableTypes(workflowId!),
    enabled: !!workflowId,
    staleTime: QUERY_CONFIG.LISTS.staleTime,
  })
}

// ============================================
// TARGET MUTATIONS
// ============================================

/**
 * Hook for creating a new asset (formerly target)
 * @param data - Object containing { seed: string } where seed is domain/IP/CIDR/URL
 */
export function useCreateTarget() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (data: { seed: string }) => targetsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.targets.all(orgId) })
      // The scannable-asset list (Create Scan step 1, RunWorkflowModal) lives
      // under a different key namespace and would otherwise serve the pre-create
      // list until it goes stale.
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.allScannableAssets(orgId) })
      toast.success('Asset created successfully.')
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for importing MANY assets at once (the paste-a-list flow).
 *
 * Invalidates exactly what `useCreateTarget` invalidates — the asset list and
 * the scannable-asset list that Create Scan step 1 and RunWorkflowModal read —
 * because an import is a get-or-create of the same resource, just in bulk.
 *
 * Deliberately SILENT, unlike `useCreateTarget`:
 *
 * - success is reported by the dialog, which has to say how many rows came back
 *   and of what type (the response can be shorter than the list sent, since two
 *   spellings of one asset collapse), and
 * - the 400s are per-ITEM — a list of `{index, seed, error}` that has to render
 *   beside each offending value. `/assets/import/` is therefore opted out of the
 *   global error toast in SetupAxios.tsx; a toast could only flatten that list
 *   into one unusable line.
 */
export function useImportAssets() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (seeds: string[]) => targetsApi.importAssets(seeds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.targets.all(orgId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.scans.allScannableAssets(orgId) })
    },
  })
}

/**
 * Hook for updating a target
 */
export function useUpdateTarget() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: ({
      targetId,
      data,
    }: {
      targetId: string
      data: Partial<{ title: string; description: string; type: string }>
    }) => targetsApi.update(targetId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.targets.all(orgId) })
      toast.success('Target updated successfully')
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for deleting a target
 */
export function useDeleteTarget() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (targetId: string) => targetsApi.delete(targetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.targets.all(orgId) })
      toast.success('Target deleted successfully')
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for verifying target via DNS TXT record
 */
export function useVerifyTargetDNS() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (targetId: string) => targetsApi.verifyDNS(targetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.targets.all(orgId) })
      toast.success('Verification completed successfully.')
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for verifying target via file upload
 */
export function useVerifyTargetFile() {
  const orgId = useAuthStore((state) => state.orgId)
  return useMutation({
    mutationFn: (targetId: string) => targetsApi.verifyFile(targetId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.targets.all(orgId) })
      toast.success('Verification completed successfully.')
    },
    onError: (error: any) => {

    },
  })
}

/**
 * Hook for fetching target verification code
 */
export function useTargetVerificationCode(targetId: string | null) {
  const orgId = useAuthStore((state) => state.orgId)
  return useQuery({
    queryKey: queryKeys.targets.verificationCode(orgId, targetId || ''),
    queryFn: () => targetsApi.getVerificationCode(targetId!),
    enabled: !!targetId && !!orgId,
  })
}
